/**
 * React Query keys and functions for play content data
 *
 * This file centralizes all query keys and API functions for React Query.
 * Using query key factories ensures consistent cache keys across the app.
 */

import { useQueries, useQuery } from '@tanstack/react-query'
import type { Drawing } from '@/types'
import type { ResolvedBlock } from '@/types/blocks'
import { buildPaginationParams } from '@/api/utils/PaginationParams'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'
import { playerBlocksToPlayers } from '@/adapters/PlayerAdapter'
import { drawingBlocksToDrawings } from '@/adapters/DrawingAdapter'

// ============================================================================
// TYPES
// ============================================================================

type Player = {
	id: string
	x: number
	y: number
	label: string
	color: string
}

export type PlayContent = {
	id: string
	name: string
	teamId: string | null
	players: Player[]
	drawings: Drawing[]
}

export type PlayListItem = {
	id: string
	name: string
	play_type: string | null
	formation_id: string | null
	personnel_id: string | null
	defensive_formation_id: string | null
	updated_at: Date
	display_order: number
	labels: Array<{ id: string; name: string; color: string }>
	drawings: Drawing[]
	players: Player[]
}

export type PlayPage = {
	items: PlayListItem[]
	nextCursor: string | null
}

// ============================================================================
// QUERY KEY FACTORIES
// ============================================================================

export const playKeys = {
	all: ['plays'] as const,
	lists: () => [...playKeys.all, 'list'] as const,
	list: (playbookId: string) => [...playKeys.lists(), playbookId] as const,
	content: (id: string) => ['plays', 'content', id] as const,
	batch: (ids: string[]) => ['plays', 'batch', [...ids].sort().join(',')] as const,
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		if (response.status === 401) {
			throw new Error('UNAUTHORIZED')
		}
		if (response.status === 403) {
			throw new Error('ACCESS_DENIED')
		}
		if (response.status === 404) {
			throw new Error('NOT_FOUND')
		}
		const error = await response.text()
		throw new Error(error || `HTTP ${response.status}`)
	}
	return response.json()
}

export async function fetchPlayContent(playId: string): Promise<PlayContent> {
	const response = await secureFetch(`/api/blocks/${playId}/resolved`)
	const data = await handleResponse<{ resolvedBlock: ResolvedBlock }>(response)
	const resolved = data.resolvedBlock

	const validPlayers = resolved.players.filter(p => p?.properties != null)

	return {
		id: resolved.block.id,
		name: (resolved.block.properties as { name?: string })?.name ?? 'Untitled',
		teamId: null,
		players: playerBlocksToPlayers(validPlayers),
		drawings: drawingBlocksToDrawings(resolved.drawings),
	}
}

export async function fetchMultiplePlays(playIds: string[]): Promise<Record<string, PlayContent>> {
	if (playIds.length === 0) {
		return {}
	}

	const uniqueIds = Array.from(new Set(playIds))
	const response = await secureFetch(`/api/plays/batch?ids=${uniqueIds.join(',')}`)
	const data = await handleResponse<{ plays: Record<string, PlayContent> }>(response)
	return data.plays
}

export async function fetchPlaybookPlaysPage(
	playbookId: string,
	limit: number = 20,
	cursor?: string,
): Promise<PlayPage> {
	const params = buildPaginationParams({ limit, cursor })

	const response = await secureFetch(`/api/playbooks/${playbookId}/plays?${params}`)
	const data = await handleResponse<PlayPage>(response)
	return data
}

export async function deletePlay(playId: string): Promise<void> {
	const response = await secureFetch(`/api/plays/${playId}`, {
		method: 'DELETE',
	})
	await handleResponse<void>(response)
}

export async function updatePlay(playId: string, updates: Record<string, any>): Promise<void> {
	const response = await secureJsonFetch(`/api/plays/${playId}`, {
		method: 'PUT',
		body: updates,
	})
	await handleResponse<void>(response)
}

export async function duplicatePlay(playId: string): Promise<PlayListItem> {
	const response = await secureJsonFetch(`/api/plays/${playId}/duplicate`, {
		method: 'POST',
	})
	const data = await handleResponse<{ play: PlayListItem }>(response)
	return data.play
}

// ============================================================================
// REACT QUERY HOOKS
// ============================================================================

const FIVE_MINUTES_MS = 5 * 60 * 1000
const TEN_MINUTES_MS = 10 * 60 * 1000

type UsePlayContentOptions = {
	enabled?: boolean
}

/**
 * Fetches full play content from the API including players and drawings.
 * Used by PlayViewerModal to get data for animation.
 *
 * Implements caching with React Query:
 * - staleTime: 5 minutes (play content doesn't change often)
 * - cacheTime: 10 minutes (keeps data in cache even when unused)
 */
export function usePlayContent(
	playId: string | null,
	options: UsePlayContentOptions = {},
) {
	const { enabled = true } = options

	return useQuery({
		queryKey: playKeys.content(playId || ''),
		queryFn: () => {
			if (!playId) {
				throw new Error('Play ID is required')
			}
			return fetchPlayContent(playId)
		},
		enabled: enabled && playId !== null,
		staleTime: FIVE_MINUTES_MS,
		gcTime: TEN_MINUTES_MS,
		retry: 1,
	})
}

/**
 * Fetches multiple plays using the batch API endpoint.
 * Returns individual query results for each play, with shared caching.
 *
 * This is more efficient than fetching plays individually:
 * - Reduces HTTP requests from N to 1
 * - All plays share the same React Query cache
 * - Individual plays can be accessed from cache by other components
 */
export function useMultiplePlays(playIds: string[], options: UsePlayContentOptions = {}) {
	const { enabled = true } = options

	return useQueries({
		queries: playIds.map((playId) => ({
			queryKey: playKeys.content(playId),
			queryFn: () => fetchPlayContent(playId),
			enabled: enabled && playIds.length > 0,
			staleTime: FIVE_MINUTES_MS,
			gcTime: TEN_MINUTES_MS,
			retry: 1,
		})),
	})
}

/**
 * Prefetches multiple plays in a single batch request.
 * Useful for preloading plays that will be needed soon.
 */
export async function prefetchMultiplePlays(
	playIds: string[],
	queryClient: any,
): Promise<void> {
	if (playIds.length === 0) {
		return
	}

	const playsContent = await fetchMultiplePlays(playIds)

	// Populate individual play caches
	Object.entries(playsContent).forEach(([id, content]) => {
		queryClient.setQueryData(playKeys.content(id), content)
	})
}
